import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, LoadingButton, useToastActions, ConfirmationModal, DescriptionList, SettingsSaveBar, useDirtyForm } from '@airclou/ui';
import { Trash2, Briefcase, AlertTriangle } from 'lucide-react';
import { updateWorkspace, deleteWorkspace, uploadWorkspaceLogo, removeWorkspaceLogo, type Workspace, type UpdateWorkspaceInput } from '@/api/workspaces';
import { getServiceUrl } from '@airclou/utils';
import { useNavigate } from 'react-router-dom';
import { useTimezone } from '@airclou/shell/timezone'
import { formatDateTimeInTz } from '@airclou/utils'
import { useAuth } from '../../contexts/AuthContext'
import { canManageOrg } from '../../navigation'

interface WorkspaceSettingsTabProps {
  workspace: Workspace;
  onUpdate: () => void;
  /**
   * The workspace row as the server just returned it. The parent merges it into its
   * copy instead of refetching the page, so a logo change repaints only the logo.
   */
  onWorkspaceChange: (workspace: Workspace) => void;
}

export function WorkspaceSettingsTab({ workspace, onUpdate, onWorkspaceChange }: WorkspaceSettingsTabProps) {
  const timezone = useTimezone()
  const { t } = useTranslation('dashboard');
  const { user, isLoading: isAuthLoading } = useAuth();

  // Two different permissions on one tab, as on the company settings tab:
  // renaming is `PATCH /api/workspaces/:id` (MANAGE) and deleting is
  // `DELETE /api/workspaces/:id` (OWNER_ONLY, because a company delete cascades
  // into these rows). Disabled rather than hidden, so the values stay readable.
  const canManage = canManageOrg(user?.orgRole ?? undefined);
  const isOwner = user?.orgRole === 'owner';

  // "We could not confirm your role" is not the claim "you may not" — `orgRole`
  // is undefined while /context is in flight AND when it failed.
  const roleKnown = !isAuthLoading && user?.orgRole !== undefined;
  const navigate = useNavigate();
  const { success, error } = useToastActions();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  // Name and slug are an explicit-Save form. The logo below is deliberately not
  // part of it: it has its own upload/remove endpoints that take effect
  // immediately, and it is a standalone control rather than a form field.
  const form = useDirtyForm<UpdateWorkspaceInput>({
    name: workspace.name,
    slug: workspace.slug,
  });
  const formData = form.values;
  const hasChanges = form.isDirty;

  // Logo upload state
  const [logoUrl, setLogoUrl] = useState<string | null>(workspace.logo_url || null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);

  // Resync the form only when the name/slug themselves change, so a logo change
  // (or any unrelated refresh) doesn't wipe edits in progress.
  useEffect(() => {
    form.setBaseline({
      name: workspace.name,
      slug: workspace.slug,
    });
  }, [workspace.id, workspace.name, workspace.slug]);

  useEffect(() => {
    setLogoUrl(workspace.logo_url || null);
  }, [workspace.logo_url]);

  const handleChange = (field: keyof UpdateWorkspaceInput, value: string) => {
    form.setField(field, value);
  };

  const validationMessage = useMemo<string | null>(
    () => (formData.name?.trim() && formData.slug?.trim() ? null : t('companies.nameSlugRequired')),
    [formData.name, formData.slug, t]
  );

  const handleSave = async () => {
    if (validationMessage) {
      error(validationMessage, t('companies.validationError'));
      return;
    }

    setIsSaving(true);
    try {
      await updateWorkspace(workspace.id, formData);
      success(t('workspaces.workspaceUpdated'), t('common.success'));
      form.setBaseline(formData);
      onUpdate();
    } catch (err: any) {
      error(err.message || t('workspaces.failedToUpdate'), t('common.error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteWorkspace(workspace.id);
      success(t('workspaces.workspaceDeleted'), t('common.success'));
      navigate(`/companies/${workspace.company_id}`);
    } catch (err: any) {
      error(err.message || t('workspaces.failedToUpdate'), t('common.error'));
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };

  const handleReset = () => {
    // Back to the baseline, not to the `workspace` prop. The prop lags a save
    // until the parent's refetch lands, so resetting from it could restore the
    // pre-save name and then declare that state clean.
    form.reset();
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setLogoError(t('organization.invalidFileType'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setLogoError(t('organization.fileTooLarge'));
      return;
    }

    setIsUploadingLogo(true);
    setLogoError(null);

    try {
      // Upload logo file
      const { url: newLogoUrl } = await uploadWorkspaceLogo(workspace.id, file);

      // Update workspace with new logo URL
      const updated = await updateWorkspace(workspace.id, { logo_url: newLogoUrl });

      setLogoUrl(newLogoUrl);
      success(t('workspaces.workspaceUpdated'));
      // Only the logo changed — hand the updated row up rather than refetching the page.
      onWorkspaceChange(updated.workspace);
    } catch (err: any) {
      const code = err.message || 'UPLOAD_FAILED';
      if (code === 'FILE_TOO_LARGE') {
        setLogoError(t('organization.fileTooLarge'));
      } else if (code === 'INVALID_MIME_TYPE') {
        setLogoError(t('organization.invalidFileType'));
      } else {
        setLogoError(t('organization.failedToUploadLogo'));
      }
    } finally {
      setIsUploadingLogo(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleLogoRemove = async () => {
    setIsUploadingLogo(true);
    setLogoError(null);

    try {
      const updated = await removeWorkspaceLogo(workspace.id, logoUrl);
      setLogoUrl(null);
      success(t('workspaces.workspaceUpdated'));
      onWorkspaceChange(updated.workspace);
    } catch {
      setLogoError(t('organization.failedToUploadLogo'));
    } finally {
      setIsUploadingLogo(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      {/* General Settings */}
      <div className="bg-card rounded-lg border border-border">
        <div className="p-6 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">{t('workspaces.generalSettings')}</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('workspaces.basicInfo')}
          </p>
        </div>
        <div className="p-6 space-y-6">
          {/* Workspace Logo */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('workspaces.workspaceLogo')}
            </label>
            <div className="flex items-start gap-4">
              <div className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center bg-gray-50 dark:bg-gray-800 overflow-hidden">
                {logoUrl ? (
                  <img
                    src={logoUrl.startsWith('http') ? logoUrl : `${getServiceUrl('dashboard')}${logoUrl}`}
                    alt="Workspace logo"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <Briefcase className="h-8 w-8 text-gray-400" />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <label htmlFor="ws-logo-upload">
                    <input
                      id="ws-logo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      // Gated on its own, not just via the button above it. The
                      // input is INSIDE the label, and a disabled button carries
                      // `pointer-events: none` — so the click falls through to
                      // the label, which activates the input regardless. A
                      // greyed-out button is not a gate when the thing it
                      // triggers is still live.
                      disabled={isUploadingLogo || isSaving || !canManage}
                      className="hidden"
                    />
                    <LoadingButton
                      onClick={() => document.getElementById('ws-logo-upload')?.click()}
                      loading={isUploadingLogo}
                      loadingText={t('workspaces.uploading')}
                      disabled={isUploadingLogo || isSaving || !canManage}
                      variant="outline"
                      size="sm"
                    >
                      {logoUrl ? t('workspaces.changeLogo') : t('workspaces.uploadLogo')}
                    </LoadingButton>
                  </label>
                  {logoUrl && (
                    <Button
                      onClick={handleLogoRemove}
                      disabled={isUploadingLogo || isSaving || !canManage}
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                    >
                      {t('common.remove')}
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('workspaces.logoHint')}
                </p>
                {logoError && (
                  <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {logoError}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="workspace-name" className="block text-sm font-medium text-foreground mb-2">
              {t('workspaces.workspaceName')}
            </label>
            <Input
              id="workspace-name"
              type="text"
              value={formData.name || ''}
              onChange={(value) => handleChange('name', value)}
              placeholder="e.g., Engineering Team"
              disabled={isSaving || !canManage}
            />
          </div>

          <div>
            <label htmlFor="workspace-slug" className="block text-sm font-medium text-foreground mb-2">
              {t('workspaces.workspaceSlug')}
            </label>
            <Input
              id="workspace-slug"
              type="text"
              value={formData.slug || ''}
              onChange={(value) => handleChange('slug', value)}
              placeholder="e.g., engineering-team"
              disabled={isSaving || !canManage}
            />
            <p className="mt-2 text-xs text-muted-foreground">
              {t('workspaces.slugHint')}
            </p>
          </div>
          {roleKnown && !canManage && (
            <p className="text-sm text-muted-foreground">
              {t('workspaces.onlyAdminsCanEdit')}
            </p>
          )}
          {!roleKnown && !isAuthLoading && (
            <p className="text-sm text-muted-foreground">
              {t('workspaces.roleUnknownReload')}
            </p>
          )}
        </div>
      </div>

      {/* Workspace Information */}
      <DescriptionList
        title={t('workspaces.workspaceInfo')}
        description={t('workspaces.additionalDetails')}
        items={[
          {
            term: t('workspaces.workspaceId'),
            description: (
              <code className="text-xs font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                {workspace.id}
              </code>
            ),
          },
          {
            term: t('workspaces.company'),
            description: workspace.company_name || workspace.company_id,
          },
          {
            term: t('common.created'),
            description: formatDateTimeInTz(workspace.created_at, timezone),
          },
          {
            term: t('workspaces.lastUpdated'),
            description: formatDateTimeInTz(workspace.updated_at, timezone),
          },
          {
            term: t('workspaces.tenantId'),
            description: (
              <code className="text-xs font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                {workspace.tenant_id}
              </code>
            ),
          },
          ...(workspace.user_count !== undefined
            ? [{
                term: t('common.members'),
                description: `${workspace.user_count}`,
              }]
            : []),
        ]}
      />

      {/* Danger Zone */}
      <div className="bg-card rounded-lg border border-red-200 dark:border-red-800">
        <div className="p-6 border-b border-red-200 dark:border-red-800">
          <h3 className="text-lg font-semibold text-red-600 dark:text-red-400">{t('workspaces.dangerZone')}</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('workspaces.irreversibleActions')}
          </p>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-foreground">{t('workspaces.deleteThisWorkspace')}</h4>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('workspaces.deleteWorkspaceWarning')}
              </p>
            </div>
            <Button
              variant="outline"
              disabled={isSaving || !isOwner}
              onClick={() => setIsDeleteModalOpen(true)}
              className="text-red-600 hover:text-red-700 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border-red-200 dark:border-red-800"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {t('workspaces.deleteWorkspace')}
            </Button>
          </div>
          {/* Both arms, because `!isOwner` is also true when the role is
              unknown — so without the second one a real owner whose
              /api/team/context call failed gets a dead Delete button and no
              reason. The details card above handles its own case; this one is
              a separate card and needs its own line. */}
          {roleKnown && !isOwner && (
            <p className="mt-4 text-sm text-muted-foreground">
              {t('workspaces.onlyOwnerCanDelete')}
            </p>
          )}
          {!roleKnown && !isAuthLoading && (
            <p className="mt-4 text-sm text-muted-foreground">
              {t('workspaces.roleUnknownReload')}
            </p>
          )}
        </div>
      </div>

      <SettingsSaveBar
        visible={hasChanges}
        onSave={handleSave}
        onDiscard={handleReset}
        isSaving={isSaving}
        // `!canManage` belongs in the set even though the disabled inputs mean
        // the form cannot currently go dirty: the gate should not be one control
        // short, or a future control that dirties the form without passing
        // through a disabled input re-opens a 403-on-save path.
        saveDisabled={isDeleting || isUploadingLogo || !canManage || Boolean(validationMessage)}
        message={validationMessage ?? t('common.unsavedChanges')}
        saveLabel={t('common.save')}
        savingLabel={t('common.saving')}
        discardLabel={t('common.discard')}
      />

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title={t('workspaces.deleteWorkspace')}
        description={t('workspaces.deleteWorkspaceConfirm', { name: workspace.name })}
        variant="danger"
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        onConfirm={handleDelete}
        isLoading={isDeleting}
      />
    </div>
  );
}
